import { useMemo } from "react";
import Button from "components/Button/Button";
import type { OptionType } from "components/inputs";
import { Select, TextInput } from "components/inputs";
import React, { useCallback } from "react";
import KaboodleModal from "components/Modals/KaboodleModal";
import Card from "components/Card/Card";
import type { SubmitHandler } from "react-hook-form";
import { useForm, Controller } from "react-hook-form";

type CMSLineItemTypeModalProps = {
  selectedDecisionId: number | null;
  isOpen: boolean;
  onClose: () => void;
  onCreate: (id: number | null, data: Record<string, string>) => Promise<void>;
};

const defaultOptions = [
  { label: "Treatment", value: "treatment" },
  { label: "Prescription", value: "prescription" },
];

type FormData = {
  name: string;
  allocationType: OptionType;
};

export const CMSLineItemTypeModal: React.FC<CMSLineItemTypeModalProps> = ({
  selectedDecisionId,
  isOpen,
  onClose,
  onCreate,
}) => {
  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (data) => {
      try {
        await onCreate(selectedDecisionId, {
          allocationType: String(data.allocationType?.value),
          name: data.name,
        });
      } catch (err) {
        console.error("Failed to create line item:", err);
      }
    },
    [onCreate, reset, onClose, history],
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const footerContent = useMemo(() => {
    return <Button onClick={handleSubmit(onSubmit)}>Save</Button>;
  }, [handleSubmit, onSubmit]);

  return (
    <KaboodleModal isOpen={isOpen} onClose={handleClose} footer={footerContent}>
      <Card heading="Create New Line Item">
        <Controller
          control={control}
          name="name"
          rules={{ required: "Name is required" }}
          render={({ field }) => (
            <TextInput
              {...field}
              placeholder="Name"
              label="Name"
              errors={errors}
            />
          )}
        />
        <Controller
          name="allocationType"
          rules={{ required: "Allocation type is required" }}
          control={control}
          render={({ field: { name, onChange } }) => (
            <Select
              name={name}
              label="Allocation Type"
              placeholder="Allocation Type"
              options={defaultOptions}
              errors={errors}
              onChange={onChange}
              required={true}
              isClearable={false}
            />
          )}
        />
      </Card>
    </KaboodleModal>
  );
};
